/**
 * Form schemas for the three Sales Pipeline modals.
 *
 * All three modals share the same client-info shape but enforce different
 * editability rules. Composing schemas keeps a single source of truth for
 * field-level validation (email format, phone length, required-ness).
 *
 *   createDealSchema       — new lead: every client field is fillable
 *   qualifyLeadSchema — only deal name is fillable, client info passes through
 *   createProofSchema      — client info passes through, product + flags fillable
 *
 * Returned types are `z.infer<typeof ...>` so the TS model and the runtime
 * validator never drift. Avoid hand-written interfaces for these payloads.
 */
import { z } from "zod";

/** Re-export shared client schema — source of truth is `@/schemas/client-info`. */
export {
  clientInfoSchema,
  type ClientInfoValues,
} from "@/schemas/client-info";

import { clientInfoSchema } from "@/schemas/client-info";

/* ── 1. New Deal (New Lead creation) ─────────────────────────────────── */

/**
 * Per spec: no deal name yet — that's assigned at Warm-Lead conversion.
 * Owner is injected from the calling component (logged-in user).
 */
export const createDealSchema = clientInfoSchema;
export type CreateDealValues = z.infer<typeof createDealSchema>;

/* ── 2. Convert to Warm Lead ─────────────────────────────────────────── */

/**
 * Deal name is a free-form display label — NOT unique (kit@, 2026-05-17).
 * Reps reuse the same label across recurring chapter orders (e.g.
 * "Phi Mu Bid Day" every season). The stable identifier is the generated
 * deal_id; the DB unique index was dropped in migration
 * 20260517090000_drop_deal_name_unique.sql. Validation here is format-only.
 */
export const qualifyLeadSchema = z.object({
  dealName: z
    .string()
    .trim()
    .min(3, "Deal name must be at least 3 characters")
    .max(64, "Deal name too long")
    .regex(
      /^[\w .\-—/]+$/u,
      "Use letters, numbers, spaces, dashes, dots, or slashes",
    ),
});

export type QualifyLeadValues = z.infer<typeof qualifyLeadSchema>;

/* ── 3. Create Proof ─────────────────────────────────────────────────── */

/**
 * 2026-05-14: replaced the Product A/B/C enum with a real catalog picker.
 * 2026-05-15 (kit@): proofs now carry MULTIPLE products. Each line has its
 * own colorway (you can ship the same design on a tee, hoodie, AND
 * sweatshirt). Design instructions and licensing flags remain shared
 * across the whole proof (one design applied to all items).
 * 2026-05-15 (kit@): each line now carries a per-size quantity breakdown
 * (one design is ordered in a spread of sizes), an optional blank
 * garment-template PDF (designFileUrl, sourced from the catalog), a
 * product `tier` (basic|premium), and a multi-select `customizations`
 * list of option names (pre-filled from the catalog quick-pick, filtered
 * by tier). The old scalar `quantity` is gone — the size rows are the
 * source of truth; the mutation sums them for any back-compat list view.
 *
 * Conditional licensing fields:
 *   - greekOrgId required when licensingNeeded is on
 *   - universityId required when collegiate is on
 */
export const proofProductLineSchema = z.object({
  categoryId: z.string().uuid(),
  categoryName: z.string().min(1),
  subItemId: z.string().uuid(),
  subItemName: z.string().min(1),
  imageUrl: z.string().optional().nullable(),
  priceCents: z.number().nullable().optional(),
  designFileUrl: z.string().nullable().optional(),
  /**
   * Sizes are HIDDEN at the proof stage (2026-05-17, kit@) — a proof only
   * pins the design + colour + customization. The per-size quantity
   * breakdown is captured later when an actual order is created. The schema
   * (and request_products.size_breakdown) is kept so the order stage can
   * resume it without a migration; it just defaults to empty here.
   */
  sizes: z
    .array(
      z.object({
        size: z.enum([
          "2XS",
          "XS",
          "S",
          "M",
          "L",
          "XL",
          "2XL",
          "3XL",
          "4XL",
          "5XL",
        ]),
        quantity: z.coerce
          .number({ invalid_type_error: "Quantity must be a number" })
          .int("Whole units only")
          .min(1, "Min 1")
          .max(99999),
      }),
    )
    .default([]),
  /**
   * Per-product multi-select customization option NAMES (filtered by tier in
   * the UI). Persisted to request_products.customization_options (text[]).
   */
  customizations: z.array(z.string()).default([]),
  /**
   * Per-product decoration option NAMES (Studs, Laces, …) — same model as
   * customizations, configured separately. Persisted to
   * request_products.decoration_options (text[]).
   */
  decorationOptions: z.array(z.string()).default([]),
  /**
   * Drives which customization options are selectable (basic = only options
   * flagged available_for_basic). The catalog tier is "basic" | "exclusive"
   * (Habits Exclusives); it is mapped to the legacy "basic" | "premium"
   * request_products.product_type vocabulary at submit time.
   */
  tier: z.enum(["basic", "exclusive"]).default("basic"),
  /**
   * Chosen colour NAME (from the product's configured colours, or free text
   * fallback). Persisted to request_products.colorway.
   */
  colorway: z
    .string()
    .trim()
    .min(1, "Pick a colour"),

  /* ── Per-product proof fields (2026-05-18, kit@) ──────────────────────
   * Design Instructions / Licensing / Collegiate / inspiration moved from
   * per-SUBMISSION to per-PRODUCT. Each line carries its own. Persisted to
   * the matching request_products columns (see useSubmitProofMutation).
   */
  designInstructions: z
    .string()
    .trim()
    .min(1, "Add design instructions for this product"),
  licensingNeeded: z.boolean().default(false),
  greekOrgId: z.string().uuid().optional().or(z.literal("")).default(""),
  collegiate: z.boolean().default(false),
  universityId: z.string().uuid().optional().or(z.literal("")).default(""),
  /** request_products.front_inspiration_url (single public URL). */
  frontInspirationUrl: z.string().optional().nullable(),
  /** request_products.back_inspiration_url (single public URL). */
  backInspirationUrl: z.string().optional().nullable(),
  /** Serialized to request_products.additional_inspiration_url (JSON array).
   *  `size` (bytes) is optional UI metadata — persisted in the JSON but not
   *  read back by parseInspirationUrls (url/name only), which is fine. */
  additionalInspiration: z
    .array(
      z.object({
        url: z.string(),
        name: z.string().optional(),
        size: z.number().optional(),
      }),
    )
    .default([]),
});

export type ProofProductLine = z.infer<typeof proofProductLineSchema>;

/**
 * Per-product conditional licensing rules. Lives on the line schema (not the
 * top-level form) now that licensing/collegiate are per product. `path` is
 * relative to the line, so RHF surfaces the error on the right product card.
 */
export const refineProofProductLine = (
  line: ProofProductLine,
  ctx: z.RefinementCtx,
) => {
  if (line.licensingNeeded && !line.greekOrgId) {
    ctx.addIssue({
      path: ["greekOrgId"],
      code: z.ZodIssueCode.custom,
      message: "Pick a Greek organization for licensing",
    });
  }
  if (line.collegiate && !line.universityId) {
    ctx.addIssue({
      path: ["universityId"],
      code: z.ZodIssueCode.custom,
      message: "Pick a university for collegiate licensing",
    });
  }
};

export const createProofSchema = z.object({
  /**
   * Optional event metadata — used by the client "Request a Design" wizard
   * (Step 1: event name + date). The staff proof flow leaves these undefined;
   * the wizard gates step navigation on them rather than the schema, so both
   * shells can share this one form shape (CreateProofValues).
   */
  eventName: z.string().trim().optional(),
  eventDate: z.string().optional(),
  products: z
    .array(proofProductLineSchema.superRefine(refineProofProductLine))
    .min(1, "Add at least one product to the proof"),
});

export type CreateProofValues = z.infer<typeof createProofSchema>;
